import { useState } from 'react';
import { api } from '../../lib/api';
import { Input } from '../ui';
import { SectionShell } from './SectionShell';
import type { SectionStatus, TenantSectionProps } from './types';

export interface IdentityValue {
  tenantId?: string;
  name?: string;
}

export function identityStatus(value: IdentityValue): SectionStatus {
  return value.tenantId && value.name ? 'complete' : 'empty';
}

/**
 * Identidad — quién es este cliente dentro de la plataforma.
 *
 * En wizard crea el tenant (POST /admin/tenants). En edición es de solo
 * lectura: el identificador es la clave de todos sus datos y la API todavía no
 * expone un endpoint para renombrar.
 */
export function IdentitySection({
  mode,
  value,
  onSaved,
  testId,
}: TenantSectionProps<IdentityValue>) {
  const [tenantId, setTenantId] = useState(value?.tenantId ?? '');
  const [name, setName] = useState(value?.name ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // El chip refleja lo guardado, no lo tipeado.
  const [savedValue, setSavedValue] = useState<IdentityValue>({
    tenantId: value?.tenantId,
    name: value?.name,
  });

  const readOnly = mode === 'edit';

  async function save() {
    if (!tenantId.trim() || !name.trim()) {
      setError('El identificador y el nombre visible son obligatorios.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await api.tenants.create({
        tenantId: tenantId.trim(),
        name: name.trim(),
        botMode: 'informational',
      });
      setSavedValue({ tenantId: tenantId.trim(), name: name.trim() });
      onSaved?.({ tenantId: tenantId.trim(), name: name.trim(), botMode: 'informational' });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionShell
      mode={mode}
      testId={testId}
      title="Identidad"
      description="Cómo se identifica este cliente dentro de la plataforma."
      status={identityStatus(savedValue)}
      error={error}
      saving={saving}
      saveDisabled={!tenantId.trim() || !name.trim()}
      onSave={readOnly ? undefined : save}
    >
      <Input
        label="Identificador"
        id="tenant-slug"
        type="text"
        placeholder="ej. mi-clinica"
        helper="Solo minúsculas y guiones. No se puede cambiar: es la clave de todos sus datos."
        value={tenantId}
        onChange={(e) => setTenantId(e.target.value)}
        disabled={saving || readOnly || !!value?.tenantId}
      />

      <Input
        label="Nombre visible"
        id="tenant-name"
        type="text"
        placeholder="ej. Clínica Norte"
        helper="Aparece en el panel y en los reportes."
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={saving || readOnly}
      />

      {readOnly && (
        <div className="toast toast-info">
          <div className="toast-description">
            El nombre y el identificador no se editan desde acá todavía: la API no expone un
            endpoint para renombrar un tenant.
          </div>
        </div>
      )}
    </SectionShell>
  );
}
